import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { getStudentProgress, updateAssignmentVisibility } from '../../services/api';
import './StudentProgress.css';
import ErrorCard from '../../components/ErrorCard/ErrorCard';

const StudentProgress = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { handleRequest } = useApi();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [studentData, setStudentData] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [editingVisibility, setEditingVisibility] = useState(null);
  const [visibilityValue, setVisibilityValue] = useState('');

  useEffect(() => {
    if (!user || (user.role !== 'teacher' && user.role !== 'admin' && user.role !== 'superadmin')) {
      navigate('/dashboard');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setErrorMessage('');
        const response = await handleRequest(getStudentProgress, studentId);
        setStudentData(response.data.student);
        setProgressData(response.data);
      } catch (error) {
        setErrorMessage(error.message || 'Kunne ikke laste inn elevens fremdrift');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, navigate, handleRequest, studentId]);

  const formatVisibilityLabel = (visibleFrom) => {
    if (!visibleFrom) return 'Synlig nå';
    const parsed = new Date(visibleFrom);
    if (Number.isNaN(parsed.getTime())) return 'Synlig nå';
    const now = new Date();
    if (parsed > now) {
      return `Synlig fra ${parsed.toLocaleString('no-NO')}`;
    }
    return 'Synlig nå';
  };

  const isFutureAssignment = (visibleFrom) => {
    if (!visibleFrom) return false;
    const parsed = new Date(visibleFrom);
    if (Number.isNaN(parsed.getTime())) return false;
    return parsed > new Date();
  };

  const handleEditVisibility = (assignment) => {
    setEditingVisibility(assignment.assignmentId);
    if (assignment.visibleFrom) {
      const date = new Date(assignment.visibleFrom);
      const localDateTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setVisibilityValue(localDateTime);
    } else {
      setVisibilityValue('');
    }
  };

  const handleSaveVisibility = async (assignmentId) => {
    try {
      const visibleFrom = visibilityValue ? new Date(visibilityValue).toISOString() : null;
      await handleRequest(updateAssignmentVisibility, assignmentId, visibleFrom);
      
      // Reload data
      const response = await handleRequest(getStudentProgress, studentId);
      setProgressData(response.data);
      setEditingVisibility(null);
      setVisibilityValue('');
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke oppdatere synlighet');
    }
  };

  const handleCancelEdit = () => {
    setEditingVisibility(null);
    setVisibilityValue('');
  };

  if (loading) {
    return (
      <div className="student-progress-container">
        <div className="loading">Laster elevens fremdrift...</div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="student-progress-container">
        <ErrorCard message={errorMessage} />
        <button className="back-btn" onClick={() => navigate(-1)}>
          Tilbake
        </button>
      </div>
    );
  }

  if (!progressData) {
    return (
      <div className="student-progress-container">
        <div className="no-data">Ingen fremdriftsdata funnet for denne eleven.</div>
        <button className="back-btn" onClick={() => navigate(-1)}>
          Tilbake
        </button>
      </div>
    );
  }

  const { student, summary, assignments, quizResults, programmingSubmissions, interactiveLessonProgress } = progressData;

  return (
    <div className="student-progress-container">
      <div className="student-progress-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          ← Tilbake
        </button>
        <div className="student-info-header">
          <h1>{student.name}</h1>
          <p className="student-email-header">{student.email}</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-card-label">Totalt tildelt</div>
          <div className="summary-card-value">{summary.totalAssignments}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">Fullført</div>
          <div className="summary-card-value">{summary.completedAssignments}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">Quizforsøk</div>
          <div className="summary-card-value">{summary.totalQuizAttempts}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">Snitt quizscore</div>
          <div className="summary-card-value">{summary.avgQuizScore}%</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">Prog. innleveringer</div>
          <div className="summary-card-value">{summary.totalProgrammingSubmissions}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">Fullførte oppgaver</div>
          <div className="summary-card-value">{summary.totalCompletedTasks}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">Interaktive leksjoner startet</div>
          <div className="summary-card-value">{summary.totalInteractiveLessonsStarted || 0}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">Interaktive leksjoner fullført</div>
          <div className="summary-card-value">{summary.totalInteractiveLessonsCompleted || 0}</div>
        </div>
      </div>

      {/* Assignments Table */}
      <div className="progress-section">
        <h2>Tildelinger</h2>
        {assignments.length > 0 ? (
          <div className="table-container">
            <table className="progress-table">
              <thead>
                <tr>
                  <th>Ressurs</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Resultat</th>
                  <th>Forsøk</th>
                  <th>Synlighet</th>
                  <th>Handlinger</th>
                </tr>
              </thead>
              <tbody>
                {assignments.map((assignment) => {
                  const isFuture = isFutureAssignment(assignment.visibleFrom);
                  return (
                    <tr 
                      key={assignment.assignmentId}
                      className={`${assignment.isCompleted ? 'completed-row' : 'pending-row'} ${isFuture ? 'future-assignment' : ''}`}
                    >
                      <td className="resource-title">
                        {assignment.resourceTitle}
                        {isFuture && <span className="future-badge" title="Fremtidig tildeling">Fremtidig</span>}
                      </td>
                      <td>
                        <span className="resource-type-badge">
                          {assignment.resourceType === 'Lesson' ? 'Quiz' : 
                           assignment.resourceType === 'ProgrammingLesson' ? 'Programmering' : 
                           'Interaktiv'}
                        </span>
                      </td>
                      <td>
                        {assignment.isCompleted ? (
                          <span className="status-badge status-completed">Fullført</span>
                        ) : (
                          <span className="status-badge status-pending">Ikke fullført</span>
                        )}
                      </td>
                      <td>
                        {assignment.resourceType === 'Lesson' ? (
                          assignment.score !== null ? `${assignment.score}%` : 'N/A'
                        ) : assignment.resourceType === 'ProgrammingLesson' ? (
                          assignment.totalTasks > 0 
                            ? `${assignment.completedTasks}/${assignment.totalTasks} oppgaver`
                            : 'N/A'
                        ) : assignment.resourceType === 'InteractiveLesson' ? (
                          assignment.totalTasks > 0
                            ? `${assignment.completedTasks}/${assignment.totalTasks} slides (${assignment.score || 0}%)`
                            : assignment.score !== null ? `${assignment.score}%` : 'N/A'
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td>{assignment.attempts || 0}</td>
                      <td>
                        {editingVisibility === assignment.assignmentId ? (
                          <div className="visibility-edit">
                            <input
                              type="datetime-local"
                              value={visibilityValue}
                              onChange={(e) => setVisibilityValue(e.target.value)}
                              className="visibility-input"
                            />
                            <div className="visibility-edit-actions">
                              <button
                                className="save-btn"
                                onClick={() => handleSaveVisibility(assignment.assignmentId)}
                                title="Lagre"
                              >
                                ✓
                              </button>
                              <button
                                className="cancel-btn"
                                onClick={handleCancelEdit}
                                title="Avbryt"
                              >
                                ✕
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span className="visibility-label" title={formatVisibilityLabel(assignment.visibleFrom)}>
                            {formatVisibilityLabel(assignment.visibleFrom)}
                          </span>
                        )}
                      </td>
                      <td>
                        {editingVisibility !== assignment.assignmentId && (
                          <button
                            className="edit-visibility-btn"
                            onClick={() => handleEditVisibility(assignment)}
                            title="Rediger synlighet"
                          >
                            Endre synlighet
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="no-data">Ingen tildelinger registrert for denne eleven.</p>
        )}
      </div>

      {/* Quiz Results */}
      {quizResults.length > 0 && (
        <div className="progress-section">
          <h2>Quizresultater</h2>
          <div className="table-container">
            <table className="progress-table">
              <thead>
                <tr>
                  <th>Leksjon</th>
                  <th>Score</th>
                  <th>Dato</th>
                </tr>
              </thead>
              <tbody>
                {quizResults.map((result) => (
                  <tr key={result._id}>
                    <td>{result.lessonTitle}</td>
                    <td className="score-cell">{result.score}%</td>
                    <td>{new Date(result.createdAt).toLocaleDateString('no-NO')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Programming Submissions */}
      {programmingSubmissions.length > 0 && (
        <div className="progress-section">
          <h2>Programmeringsinnleveringer</h2>
          <div className="table-container">
            <table className="progress-table">
              <thead>
                <tr>
                  <th>Leksjon</th>
                  <th>Oppgave</th>
                  <th>Status</th>
                  <th>Dato</th>
                </tr>
              </thead>
              <tbody>
                {programmingSubmissions.map((submission) => (
                  <tr key={submission._id}>
                    <td>{submission.lessonTitle}</td>
                    <td>Oppgave {submission.taskNumber}</td>
                    <td>
                      {submission.completed ? (
                        <span className="status-badge status-completed">Fullført</span>
                      ) : (
                        <span className="status-badge status-pending">Ikke fullført</span>
                      )}
                    </td>
                    <td>{new Date(submission.createdAt).toLocaleDateString('no-NO')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Interactive Lesson Progress */}
      {interactiveLessonProgress && interactiveLessonProgress.length > 0 && (
        <div className="progress-section">
          <h2>Interaktive leksjoner</h2>
          <div className="table-container">
            <table className="progress-table">
              <thead>
                <tr>
                  <th>Leksjon</th>
                  <th>Fremdrift</th>
                  <th>Status</th>
                  <th>Startet</th>
                  <th>Fullført</th>
                </tr>
              </thead>
              <tbody>
                {interactiveLessonProgress.map((progress) => (
                  <tr key={progress._id}>
                    <td>{progress.lessonTitle}</td>
                    <td>
                      {progress.totalScreens > 0 ? (
                        <span>
                          {progress.currentScreenIndex}/{progress.totalScreens} slides ({progress.completionPercentage}%)
                        </span>
                      ) : (
                        <span>{progress.completionPercentage}%</span>
                      )}
                    </td>
                    <td>
                      {progress.completed ? (
                        <span className="status-badge status-completed">Fullført</span>
                      ) : (
                        <span className="status-badge status-pending">Pågår</span>
                      )}
                    </td>
                    <td>{progress.startedAt ? new Date(progress.startedAt).toLocaleDateString('no-NO') : 'N/A'}</td>
                    <td>{progress.completedAt ? new Date(progress.completedAt).toLocaleDateString('no-NO') : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentProgress;
